/**
 * axisAngleUtils.ts — Client-side utility for computing the mean axis angle
 * from template pose frames.
 *
 * This is used by AxisLineAnnotation to surface the measured template angle
 * to the coach when reference_type === 'template_angle', so they know what
 * the default resolves to before saving.
 *
 * The angle is computed relative to the vertical axis (matching the backend's
 * AxisReferenceType.VERTICAL convention).  The displayed value is indicative —
 * the authoritative angle is computed at eval time inside axis_goal_evaluation.py.
 *
 * ≥3-frame requirement: mirrors the backend fallback guard at
 * axis_goal_evaluation.py:1107,1119 — if fewer than 3 valid frames are
 * available for the landmark pair, the fallback to `target_angle_degrees`
 * fires at eval time.  This function returns the count so the UI can show a
 * warning.
 */

import type { PoseData } from '@/utils/landmarkDetection';
import { getLandmarkPosition } from '@/utils/goalOverlayHelpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MeasuredAxisAngle {
  /** Mean angle in degrees, measured from the vertical (positive = clockwise). */
  meanAngleDeg: number;
  /** Number of frames with valid positions for both landmarks. */
  validFrameCount: number;
  /**
   * Whether the backend ≥3-frame requirement is satisfied.
   * When false, the evaluator will fall back to target_angle_degrees at eval time.
   */
  meetsMinFrames: boolean;
}

// ---------------------------------------------------------------------------
// Core computation
// ---------------------------------------------------------------------------

/**
 * Compute the mean axis angle (degrees from vertical) for a landmark pair
 * across the available pose frames, optionally restricted to a phase frame range.
 *
 * @param allPoseFrames - Map of frame_number → PoseData loaded from the template video.
 * @param landmarkStart - Snake-case landmark name for the axis start (e.g. "lead_hip").
 *                        Functional names (lead_* / trail_*) are not resolved here —
 *                        they depend on handedness and are skipped gracefully, returning null.
 * @param landmarkEnd   - Snake-case landmark name for the axis end.
 * @param phaseStartFrame - Optional inclusive start frame for the phase range.
 * @param phaseEndFrame   - Optional inclusive end frame for the phase range.
 * @returns MeasuredAxisAngle when at least 1 valid frame exists, or null when
 *          the landmarks cannot be looked up (e.g. functional names) or no
 *          valid frames exist.
 */
export function computeMeanAxisAngle(
  allPoseFrames: Map<number, PoseData> | undefined,
  landmarkStart: string,
  landmarkEnd: string,
  phaseStartFrame?: number,
  phaseEndFrame?: number,
): MeasuredAxisAngle | null {
  if (!allPoseFrames || allPoseFrames.size === 0) return null;
  if (!landmarkStart || !landmarkEnd) return null;

  // Functional landmarks (lead_* / trail_*) cannot be resolved client-side
  // without knowing handedness, so bail early and let the backend handle them.
  if (
    landmarkStart.startsWith('lead_') ||
    landmarkStart.startsWith('trail_') ||
    landmarkEnd.startsWith('lead_') ||
    landmarkEnd.startsWith('trail_')
  ) {
    return null;
  }

  // Convert the Map to the array format expected by getLandmarkPosition.
  // We only need to include frames in the phase range (or all frames).
  const frameNumbers = Array.from(allPoseFrames.keys()).sort((a, b) => a - b);

  // Build a flat array with index corresponding to frame_number for getLandmarkPosition.
  // getLandmarkPosition uses `poseData[frameIndex]` where frameIndex is the direct array index.
  // To avoid allocating a sparse array the size of the video, we instead work directly.
  const angles: number[] = [];

  for (const frameNum of frameNumbers) {
    // Apply phase filter when provided
    if (phaseStartFrame !== undefined && frameNum < phaseStartFrame) continue;
    if (phaseEndFrame !== undefined && frameNum > phaseEndFrame) continue;

    const poseDataAtFrame = allPoseFrames.get(frameNum);
    if (!poseDataAtFrame) continue;

    // getLandmarkPosition expects an array of frames where index = frame_number.
    // We pass a single-element wrapper array at index 0 to avoid a large sparse array.
    const singleFrameArray = [poseDataAtFrame];
    const startPos = getLandmarkPosition(singleFrameArray, 0, landmarkStart, false);
    const endPos = getLandmarkPosition(singleFrameArray, 0, landmarkEnd, false);

    if (!startPos || !endPos) continue;

    const dx = endPos.x - startPos.x;
    // Canvas Y increases downward; we use -dy so "up" is positive.
    const dy = -(endPos.y - startPos.y);

    // Angle from vertical: atan2(dx, dy) — 0° = pointing straight up,
    // positive = clockwise tilt when viewed on screen.
    const angleDeg = Math.atan2(dx, dy) * (180 / Math.PI);
    angles.push(angleDeg);
  }

  if (angles.length === 0) return null;

  // Circular mean to handle angle wrap-around near ±180°.
  const sinSum = angles.reduce((s, a) => s + Math.sin((a * Math.PI) / 180), 0);
  const cosSum = angles.reduce((s, a) => s + Math.cos((a * Math.PI) / 180), 0);
  const meanAngleDeg = Math.atan2(sinSum, cosSum) * (180 / Math.PI);

  return {
    meanAngleDeg: Math.round(meanAngleDeg * 10) / 10, // 1 decimal place
    validFrameCount: angles.length,
    meetsMinFrames: angles.length >= 3,
  };
}
